import { useElements, useStripe } from '@stripe/react-stripe-js';
import type {
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementReadyEvent,
} from '@stripe/stripe-js';
import { useCallback, useState } from 'react';

export type UseWebExpressCheckoutParams = {
  clientSecret: MaybeUndefined<string>;
  onComplete: (setupIntentId: string) => Promise<boolean>;
};

export type UseWebExpressCheckoutReturn = {
  isAvailable: boolean;
  onConfirm: (event: StripeExpressCheckoutElementConfirmEvent) => Promise<void>;
  onReady: (event: StripeExpressCheckoutElementReadyEvent) => void;
};

/**
 * Hook for Express Checkout Element (Google Pay / Apple Pay) using a setup intent flow.
 *
 * Must be called inside a Stripe <Elements> provider with `mode: 'setup'`.
 * The `onComplete` callback receives the confirmed setup intent ID for backend processing.
 */
export const useWebExpressCheckout = ({
  clientSecret,
  onComplete,
}: UseWebExpressCheckoutParams): UseWebExpressCheckoutReturn => {
  const stripe = useStripe();
  const elements = useElements();
  const [isAvailable, setIsAvailable] = useState(false);

  const onReady = useCallback(({ availablePaymentMethods }: StripeExpressCheckoutElementReadyEvent) => {
    setIsAvailable(Boolean(availablePaymentMethods));
  }, []);

  const onConfirm = useCallback(
    async (event: StripeExpressCheckoutElementConfirmEvent) => {
      if (!stripe || !elements || !clientSecret) {
        event.paymentFailed({ reason: 'fail' });
        return;
      }

      const { error: submitError } = await elements.submit();
      if (submitError) {
        event.paymentFailed({ reason: 'fail' });
        return;
      }

      const { error, setupIntent } = await stripe.confirmSetup({
        clientSecret,
        elements,
        redirect: 'if_required',
      });

      if (error || !setupIntent) {
        event.paymentFailed({ reason: 'fail' });
        return;
      }

      const success = await onComplete(setupIntent.id);
      if (!success) {
        event.paymentFailed({ reason: 'fail' });
      }
    },
    [clientSecret, elements, onComplete, stripe],
  );

  return { isAvailable, onConfirm, onReady };
};
